/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import './AfterLoading.less';
import { Input, Button, Dropdown, Form, Space, AutoComplete } from 'antd';
import { useHistory } from 'react-router-dom';
import { useAuth } from '@isocel/front-common';

import { usePackagingFlow } from '../../context/PackagingFlowContext';
import { BaseModel, PackagingFlowDirection, PackagingFlowItem, Store } from '../../queries/api/types';
import { useStoreByOrganization } from '../../queries/store';
import { getAllPackagingFlow } from '../../queries/api/packaging-flow';
import { useNotification } from '../notification/Notification';
import { useWarehousesByOrganization } from '../../queries/palette';

const AfterLoading: React.FC = () => {
    const history = useHistory();
    const [search, setSearch] = useState('');
    const [warehouseData, setWarehouseData] = useState<BaseModel[]>([]);
    const [visible, setVisible] = useState(false);
    const [filteredStores, setFilteredStores] = useState<Store[]>([]);
    const [showWarehouseField, setShowWarehouseField] = useState(false);
    const { showNotification } = useNotification();
    const {
        setSelectedStore,
        selectedStore,
        voucher,
        warehouse,
        setWarehouse,
        setVoucher,
        setCmr,
        setIsAfterLoading,
        setPackageFlow,
        setIsUpdatePackage,
        reset,
        setIsModifierVisible,
    } = usePackagingFlow();
    const { user } = useAuth();
    const { data: stores } = useStoreByOrganization(user?.organization?.id ?? '');
    const { data: wareHouse } = useWarehousesByOrganization(user?.organization?.id ?? '');
    const [form] = Form.useForm();

    useEffect(() => {
        reset();
    }, []);

    useEffect(() => {
        if (voucher) {
            form.setFields([{ name: 'transportNumber', errors: [] }]);
        }
    }, [voucher, form]);

    useEffect(() => {
        if (search) {
            form.setFields([{ name: 'store', errors: [] }]);
        }
    }, [search, form]);

    useEffect(() => {
        if (wareHouse) {
            setWarehouseData(wareHouse);
        }
    }, [wareHouse]);

    const handleVoucherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        form.setFieldValue('voucher', e.target.value);
        setVoucher(e.target.value);
    };

    const handleCmr = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCmr(e.target.value);
        form.setFieldValue('cmr', e.target.value);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearch(value);
        if (value && stores) {
            const filtered = stores.filter(
                (store) =>
                    store.name.toLowerCase().includes(value.toLowerCase()) ||
                    store.code.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredStores(filtered);
            setVisible(true);
        } else {
            setFilteredStores(stores || []);
            setVisible(false);
        }
    };

    const handleSelectStore = (store: Store) => {
        setSearch(`${store.code} - ${store.name}`);
        setSelectedStore(store);
        form.setFieldValue('store', store);
        setVisible(false);
    };

    const handleDropdownVisibleChange = (open: boolean) => {
        if (open) {
            setFilteredStores(stores || []);
        }
        setVisible(open);
    };

    const handleWarehouseChange = (value: string, option: any) => {
        const selectedWarehouse = warehouseData.find((warehouse) => warehouse._id === option.id);
        if (selectedWarehouse) {
            setWarehouse(selectedWarehouse);
            form.setFieldValue('entrepot', selectedWarehouse.name);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, store: Store) => {
        if (event.key === 'Enter' || event.key === ' ') {
            handleSelectStore(store);
        }
    };

    const handleNextClick = () => {
        form.validateFields().then(async () => {
            setIsAfterLoading(true);
            const response = await getAllPackagingFlow({
                transportVoucher: voucher,
                storeIds: selectedStore?.id,
                flowType: PackagingFlowDirection.ShippedToStore,
            });
            const res = response.items[0];
            if (res) {
                if (res.received === false) {
                    setIsUpdatePackage(true);
                    const updatedResponse = {
                        ...res,
                        packages: res.packages.map((pkg: PackagingFlowItem) => ({
                            ...pkg,
                            goodCount: pkg.goodCount === 0 && pkg.badCount === 0 ? pkg.shippedQuantity : pkg.goodCount,
                        })),
                    };
                    setPackageFlow(updatedResponse);
                    setIsModifierVisible(false);
                    history.push('/supportTracking');
                } else {
                    showNotification('error', "Échec de l'analyse", `Le flux a déjà été créé`);
                }
            } else {
                setIsUpdatePackage(false);
                setShowWarehouseField(true);
            }
        });
    };

    const handleFinalClick = () => {
        setIsAfterLoading(true);
        history.push('/shipmentTracking');
    };

    const dropdownOverlay = (
        <div className="dropdown-content">
            {filteredStores.length > 0 ? (
                filteredStores.map((store) => (
                    <div
                        className="dropdown-item"
                        key={store.id}
                        onClick={() => handleSelectStore(store)}
                        onKeyDown={(event) => handleKeyDown(event, store)}
                        role="button"
                        tabIndex={0}
                    >
                        {store.code} - {store.name}
                    </div>
                ))
            ) : (
                <div className="dropdown-item">Aucun résultat</div>
            )}
        </div>
    );

    return (
        <div className="after-loading-page">
            <div className="title">
                {/*  <LeftOutlined className="back-icon" onClick={() => history.goBack()} /> */}
                <h2>Veuillez saisir les informations</h2>
            </div>
            <Form
                form={form}
                name="after-loading-form"
                layout="vertical"
                onFinish={showWarehouseField ? handleFinalClick : handleNextClick}
                autoComplete="off"
            >
                <Form.Item
                    name="voucher"
                    rules={[{ required: true, message: 'Le numéro du bon de transport est requis' }]}
                >
                    <label className="field-label" htmlFor="transport-number-input">
                        N° de bon de transport
                    </label>
                    <Input
                        id="transport-number-input"
                        placeholder="Saisir le N de bon de transport"
                        className="input-field"
                        onChange={handleVoucherChange}
                    />
                </Form.Item>
                <Form.Item name="store" rules={[{ required: true, message: 'Veuillez sélectionner un magasin' }]}>
                    <label className="field-label" htmlFor="store-input">
                        Magasin
                    </label>
                    <Dropdown
                        overlay={dropdownOverlay}
                        trigger={['click']}
                        open={visible}
                        onOpenChange={handleDropdownVisibleChange}
                    >
                        <Input
                            id="store-input"
                            placeholder="Saisir le nom du magasin"
                            value={search}
                            onChange={handleSearchChange}
                            className="input-field"
                        />
                    </Dropdown>
                </Form.Item>
                <Form.Item>
                    <label className="field-label" htmlFor="cmr">
                        CMR(optionnel)
                    </label>
                    <Input id="cmr" onChange={handleCmr} placeholder="CMR number" className="input-field" />
                </Form.Item>
                {showWarehouseField && (
                    <Form.Item
                        name="entrepot"
                        rules={[{ required: true, message: 'Veuillez sélectionner un entrepôt' }]}
                    >
                        <label className="field-label" htmlFor="entrepot">
                            Entrepôt
                        </label>
                        <AutoComplete
                            className="input-field"
                            placeholder="Entrepôt"
                            notFoundContent="Aucun résultat"
                            value={warehouse?.name}
                            onChange={handleWarehouseChange}
                            options={warehouseData?.map((warehouse) => ({
                                value: warehouse.name,
                                id: warehouse._id,
                            }))}
                        />
                    </Form.Item>
                )}
                <Form.Item>
                    <Space>
                        <div className="submit-button">
                            <Button type="primary" disabled={!voucher && !search} htmlType="submit">
                                Suivant
                            </Button>
                        </div>
                    </Space>
                </Form.Item>
            </Form>
        </div>
    );
};

export default AfterLoading;
